import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { some, startCase } from 'lodash'
import { Trans } from '@lingui/macro'

import { IStepComponentProps } from '../../types/IStepComponentProps'
import { ButtonOutline, ButtonPrimary } from 'front-end/src/components'
import { staticLinks } from '../../../../../../../shared/linkBuilder'
import { ImportMappingSelector } from './ImportMappingSelector'
import { FieldMappingList } from './FieldsMappingList'
import Spinner from '../../../../../components/common/spinner/Spinner'
import { useCurrentMapping } from '../../hooks/useCurrentMapping'

import { UsersImportStep } from '../../../types/UserImportStep'
import { usePatchUsersImportMutation } from '../../operations/patchUsersImport.codegen'
import { useSetOwnUserImportFieldMapMutation } from './FieldsStepComponent.codegen'
import { navigateTo } from 'front-end/src/app/util/appHistory'

const REQUIRE_ONE_OF_FIELDS = ['email', 'account_name']

export const FieldsStepComponent: FunctionComponent<IStepComponentProps> = ({
  usersImport,
  setStep
}) => {
  const {
    currentMapping,
    onChange,
    onReplace,
    requiredField,
    requiredFieldMapped
  } = useCurrentMapping(usersImport)

  const [patchUsersImport, { loading: isPatching }] =
    usePatchUsersImportMutation()
  const [setOwnFieldMap, { loading: isSettingMap }] =
    useSetOwnUserImportFieldMapMutation()

  const onSubmit = useCallback(async () => {
    if (currentMapping.id) {
      await patchUsersImport({
        variables: { id: usersImport.id, importMappingId: currentMapping.id }
      })
    } else {
      await setOwnFieldMap({
        variables: { id: usersImport.id, map: currentMapping.map }
      })
    }

    setStep(UsersImportStep.SETTINGS)
  }, [currentMapping])

  const disableSubmit = useMemo(() => {
    if (isSettingMap || isPatching) {
      return true
    }

    const { map } = currentMapping

    return !some(map, (value) => REQUIRE_ONE_OF_FIELDS.includes(value))
  }, [isPatching, isSettingMap, currentMapping?.map])

  if (!usersImport) {
    return <Spinner />
  }

  return (
    <div className="BulkUsersImportPage__step fields-step">
      <h3>
        <Trans>Match your data to the appropriate user fields:</Trans>
      </h3>
      <div className="BulkUsersImportPage__section">
        <div className="BulkUsersImportPage__select-map form-row">
          <div className="label">
            <Trans>Select a stored field map to use</Trans>
          </div>
          <ImportMappingSelector
            selectedId={currentMapping.id}
            columnsCount={usersImport.configuration.samples.length}
            onSelect={onReplace}
          />
        </div>
      </div>
      <FieldMappingList
        onChange={onChange}
        samples={usersImport.configuration.samples}
        fieldsMap={currentMapping.map}
      />
      {!requiredFieldMapped && (
        <div className="BulkUsersImportPage__section">
          <Trans>Mapping for {startCase(requiredField)} is required</Trans>
        </div>
      )}
      <div className="BulkUsersImportPage__step-actions">
        <ButtonOutline onClick={() => navigateTo(staticLinks.userImports)}>
          <Trans>Back</Trans>
        </ButtonOutline>
        <ButtonPrimary disabled={disableSubmit} onClick={onSubmit}>
          <Trans>Continue</Trans>
        </ButtonPrimary>
      </div>
    </div>
  )
}
